import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import {
  STELLPLATZ_ARTEN_DEF,
  STELLPLATZ_LAGE,
  STELLPLATZ_LAGE_INNEN,
  type StellplatzArtKey,
} from "./enums";
import { ILLU } from "./illustrationTokens";
import {
  TIEFGARAGE_AUSDEHNUNG,
  TIEFGARAGE_DARUEBER_HINAUS,
  type StellplatzDetail,
  type StellplatzDetails,
} from "./stellplatzTypes";
import { ziffernFilter } from "./GrossZahlEingabe";

/**
 * Stellplätze **je Art** — Tiefgarage, Garage, Carport (xlsx Z. 7, Punkt 7).
 *
 * Bis hierher gab es eine Mehrfachauswahl `stellplatzArten` plus **eine**
 * gemeinsame Lage (`stellplatzLage`) und **eine** gemeinsame Anzahl
 * (`stellplatzAnzahl`) für alle angekreuzten Arten. Die Spec verlangt beides je
 * Art, deshalb liegen die Details jetzt unter `stellplatzDetails`:
 *
 *   { tiefgarage: { lage: "im Gebäude", lageWo: "Untergeschoss", anzahl: "12" },
 *     garage:     { lage: "außerhalb", anzahl: "2" } }
 *
 * **Altdaten bleiben unangetastet.** `stellplatzLage`/`stellplatzAnzahl` waren
 * eine Sammelangabe für alle Arten zusammen; sie werden deshalb *nicht* in jede
 * Art hineinkopiert (das suggerierte dreimal „10 Stellplätze"), sondern einmalig
 * als Hinweiszeile über der Liste gezeigt. Geschrieben oder gelöscht werden die
 * beiden Alt-Keys nirgends.
 *
 * Die Auswahl geschieht über **Bildkarten** (eine je Art) statt über eine
 * Ankreuzliste: „Tiefgarage" und „Carport" sind am Bild schneller zu treffen
 * als am Wort, und der Schritt bleibt in derselben Bildsprache wie Bauartklasse,
 * Dach und Stellung. Mehrfachauswahl bleibt Mehrfachauswahl (`role="checkbox"`),
 * die Folgefragen klappen unverändert unter der jeweils gewählten Art auf.
 *
 * Die Auswahl selbst bleibt in `stellplatzArten` (Array der Beschriftungen) —
 * dasselbe Format wie bisher, damit Bestandsdaten und die Vertragsmaske weiter
 * passen. Unbekannte Alt-Beschriftungen im Array bleiben erhalten: abgehakt
 * wird nur, was hier auch angeboten wird.
 *
 * Parsen und Formatieren der beiden Keys stehen in `stellplatzTypes.ts` — die
 * Vertragsmaske und die Zusammenfassung brauchen sie ohne diese Komponente.
 */

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", rowGap: "10px" },
  legacy: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    color: BRAND.ink,
    backgroundColor: "#FFF8E8",
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.borderLeft("3px", "solid", BRAND.gold),
    ...shorthands.borderRadius("4px"),
  },
  // Der Rahmen sitzt jetzt an der Bildkarte selbst — die Hülle hält nur noch
  // Karte und Folgefragen zusammen. Zwei ineinander liegende Rahmen (Karte im
  // Kasten) machten den Aktiv-Zustand sonst schwerer lesbar als den Kasten.
  card: {
    display: "flex",
    flexDirection: "column",
  },
  kopf: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    textAlign: "left",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "12px",
    paddingRight: "16px",
    display: "flex",
    alignItems: "center",
    columnGap: "14px",
    ...shorthands.border("2px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
    transitionProperty: "border-color, box-shadow",
    transitionDuration: "150ms",
    ":hover": { ...shorthands.borderColor(BRAND.purple) },
    ":focus-visible": {
      ...shorthands.outline("2px", "solid", BRAND.purple),
      outlineOffset: "2px",
    },
  },
  // Aktiv über Rahmen + Ring wie in den übrigen Bildauswahlen des Schritts.
  kopfAktiv: {
    ...shorthands.borderColor(BRAND.purple),
    boxShadow: `0 0 0 4px ${BRAND.purple}22`,
  },
  kopfBild: {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: ILLU.headerBg,
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "8px",
    paddingRight: "8px",
    ...shorthands.borderRadius("8px"),
  },
  kopfSvg: {
    width: "72px",
    height: "58px",
  },
  kopfLabel: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "1rem",
    color: BRAND.ink,
    flexGrow: 1,
  },
  detail: {
    marginTop: "10px",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "12px",
    paddingRight: "12px",
    backgroundColor: "#FFF8E8",
    ...shorthands.borderLeft("3px", "solid", BRAND.gold),
    ...shorthands.borderRadius("4px"),
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "10px",
    "@media (min-width: 640px)": {
      gridTemplateColumns: "2fr 1fr",
    },
  },
  field: {
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
    minWidth: 0,
  },
  fullSpan: {
    "@media (min-width: 640px)": { gridColumn: "1 / -1" },
  },
  fieldLabel: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.72rem",
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#5d5664",
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.92rem",
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    paddingTop: "7px",
    paddingBottom: "7px",
    paddingLeft: "10px",
    paddingRight: "10px",
    ...shorthands.borderRadius("4px"),
    ...shorthands.border("1px", "solid", "#d4cfd9"),
    outlineWidth: 0,
    transitionProperty: "border-color",
    transitionDuration: "120ms",
    ":focus": { ...shorthands.borderColor(BRAND.purple) },
  },
});

/**
 * Fahrzeug-Silhouette, gemeinsames Bauteil aller drei Motive. Der Ursprung
 * liegt vorne links am Aufbau; auf der Straße steht das Auto mit y + 21.
 */
const Fahrzeug = ({ x, y }: { x: number; y: number }) => (
  <g transform={`translate(${x} ${y})`}>
    <path
      d="M2 17v-5a3 3 0 0 1 3-3h5l7-7h16l7 7h5a3 3 0 0 1 3 3v5z"
      fill={ILLU.glassFill}
      stroke={ILLU.wallStroke}
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
    <circle cx="13" cy="17" r="4" fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth="1.2" />
    <circle cx="37" cy="17" r="4" fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth="1.2" />
  </g>
);

/**
 * Motive der drei Stellplatzarten — gleiche Bildsprache wie die übrigen
 * Illustrationen des Schritts (viewBox `0 0 100 80`, Farben aus `ILLU`).
 *
 * - Tiefgarage: Erdgleiche oben mit Einfahrt, Rampe hinunter ins Untergeschoss.
 * - Garage: geschlossener Baukörper mit Sektionaltor.
 * - Carport: Pfostendach ohne Wände über dem Fahrzeug.
 */
const MOTIVE: Record<StellplatzArtKey, (p: { className?: string }) => JSX.Element> = {
  tiefgarage: ({ className }) => (
    <svg className={className} width="100" height="80" viewBox="0 0 100 80" fill="none" aria-hidden="true" focusable="false">
      <path d="M4 26h30M70 26h26" stroke={ILLU.roofStroke} strokeWidth="1.5" strokeLinecap="round" />
      <path d="M34 26 58 46" stroke={ILLU.jointStroke} strokeWidth="1.5" strokeLinecap="round" />
      <path d="M70 26v20" stroke={ILLU.jointStroke} strokeWidth="1.5" />
      <rect x="10" y="46" width="80" height="28" fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth="1.5" />
      <Fahrzeug x={26} y={52} />
    </svg>
  ),
  garage: ({ className }) => (
    <svg className={className} width="100" height="80" viewBox="0 0 100 80" fill="none" aria-hidden="true" focusable="false">
      <path d="M4 74h92" stroke={ILLU.roofStroke} strokeWidth="1.5" strokeLinecap="round" />
      <path d="M14 34 50 16l36 18z" fill={ILLU.roofFill} stroke={ILLU.roofStroke} strokeWidth="1.5" strokeLinejoin="round" />
      <rect x="20" y="34" width="60" height="40" fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth="1.5" />
      <rect x="30" y="44" width="40" height="30" fill={ILLU.headerBg} stroke={ILLU.wallStroke} strokeWidth="1.2" />
      <path d="M30 52h40M30 60h40M30 68h40" stroke={ILLU.jointStroke} strokeWidth="1" />
    </svg>
  ),
  carport: ({ className }) => (
    <svg className={className} width="100" height="80" viewBox="0 0 100 80" fill="none" aria-hidden="true" focusable="false">
      <path d="M4 74h92" stroke={ILLU.roofStroke} strokeWidth="1.5" strokeLinecap="round" />
      <path d="M12 30h76v9H12z" fill={ILLU.roofFill} stroke={ILLU.roofStroke} strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M18 39v35M82 39v35" stroke={ILLU.timberStroke} strokeWidth="3.5" strokeLinecap="round" />
      <Fahrzeug x={26} y={53} />
    </svg>
  ),
};

const StellplatzIcon = ({
  art,
  className,
}: {
  art: StellplatzArtKey;
  className?: string;
}) => {
  const Motiv = MOTIVE[art];
  return <Motiv className={className} />;
};

type Props = {
  /** Aktuelle Auswahl (Beschriftungen), Answer-Key `stellplatzArten`. */
  arten: string[];
  /** Lage/Anzahl je Art, Answer-Key `stellplatzDetails`. */
  details: StellplatzDetails;
  /** Alt-Key `stellplatzLage` — reine Anzeige, wird nie geschrieben. */
  legacyLage?: string;
  /** Alt-Key `stellplatzAnzahl` — reine Anzeige, wird nie geschrieben. */
  legacyAnzahl?: string;
  /**
   * Auswahl und Details wandern gemeinsam nach oben: zwei getrennte
   * Schreibaufrufe rechnen beide aus dem State desselben Renders und der zweite
   * würde den ersten still überschreiben (dieselbe Falle wie in
   * `BeteiligtenListe`).
   */
  onChange: (next: { arten: string[]; details: StellplatzDetails }) => void;
  /** Auswahl für „In welchem Geschoss?" (siehe `geschossOptionen`). */
  geschosse?: string[];
};

const StellplatzBlock = ({
  arten,
  details,
  legacyLage,
  legacyAnzahl,
  onChange,
  geschosse = ["UG 1", "EG", "OG 1"],
}: Props) => {
  const styles = useStyles();

  const toggle = (key: StellplatzArtKey, label: string, checked: boolean) => {
    const naechsteArten = checked
      ? arten.includes(label)
        ? arten
        : [...arten, label]
      : arten.filter((a) => a !== label);
    const naechsteDetails = { ...details };
    // Abhaken entfernt Beschriftung UND Detaileintrag — sonst bliebe eine
    // Anzahl für eine Art stehen, die es laut Auswahl nicht mehr gibt.
    if (!checked) delete naechsteDetails[key];
    onChange({ arten: naechsteArten, details: naechsteDetails });
  };

  const setDetail = (key: StellplatzArtKey, patch: StellplatzDetail) => {
    const naechste: StellplatzDetail = { ...(details[key] ?? {}), ...patch };
    // Fläche und Geschoss gehören zur Lage im Gebäude; bei einem Wechsel nach
    // außen blieben sie sonst unsichtbar gespeichert stehen.
    if (naechste.lage !== STELLPLATZ_LAGE_INNEN) {
      delete naechste.lageWo;
      delete naechste.flaeche;
      delete naechste.geschoss;
      delete naechste.ausdehnung;
    }
    // Die Fläche außerhalb gibt es nur, wenn die Tiefgarage über das Gebäude hinausreicht.
    if (naechste.ausdehnung !== TIEFGARAGE_DARUEBER_HINAUS) delete naechste.flaecheAusserhalb;
    onChange({ arten, details: { ...details, [key]: naechste } });
  };

  const legacyText = [
    legacyAnzahl ? `${legacyAnzahl} Stellplätze` : null,
    legacyLage ?? null,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <div className={styles.root}>
      {legacyText && (
        <div className={styles.legacy}>
          Frühere Sammelangabe: {legacyText}. Sie galt für alle Arten zusammen und
          bleibt gespeichert — bitte Lage und Anzahl unten je Art erfassen.
        </div>
      )}
      {STELLPLATZ_ARTEN_DEF.map((art) => {
        const checked = arten.includes(art.label);
        const detail = details[art.key] ?? {};
        return (
          <div key={art.key} className={styles.card}>
            <button
              type="button"
              role="checkbox"
              aria-checked={checked}
              className={mergeClasses(styles.kopf, checked && styles.kopfAktiv)}
              onClick={() => toggle(art.key, art.label, !checked)}
            >
              <span className={styles.kopfBild}>
                <StellplatzIcon art={art.key} className={styles.kopfSvg} />
              </span>
              <span className={styles.kopfLabel}>{art.label}</span>
            </button>
            {checked && (
              <div className={styles.detail}>
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>Lage</span>
                  <select
                    className={styles.input}
                    name={`stellplatz-${art.key}-lage`}
                    aria-label={`${art.label} — Lage`}
                    value={detail.lage ?? ""}
                    onChange={(e) => setDetail(art.key, { lage: e.target.value })}
                  >
                    <option value="">Bitte wählen</option>
                    {STELLPLATZ_LAGE.map((l) => (
                      <option key={l} value={l}>
                        {l}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>Anzahl</span>
                  <input
                    className={styles.input}
                    name={`stellplatz-${art.key}-anzahl`}
                    aria-label={`${art.label} — Anzahl`}
                    inputMode="numeric"
                    value={detail.anzahl ?? ""}
                    onChange={(e) => setDetail(art.key, { anzahl: ziffernFilter(e.target.value) })}
                  />
                </div>
                {detail.lage === STELLPLATZ_LAGE_INNEN && (
                  <>
                    <div className={styles.field}>
                      <span className={styles.fieldLabel}>Fläche (m²)</span>
                      <input
                        className={styles.input}
                        name={`stellplatz-${art.key}-flaeche`}
                        aria-label={`${art.label} — Fläche (m²)`}
                        inputMode="decimal"
                        value={detail.flaeche ?? ""}
                        onChange={(e) =>
                          setDetail(art.key, {
                            flaeche: ziffernFilter(e.target.value, { dezimal: true }),
                          })
                        }
                      />
                    </div>
                    <div className={styles.field}>
                      <span className={styles.fieldLabel}>Geschoss</span>
                      <select
                        className={styles.input}
                        name={`stellplatz-${art.key}-geschoss`}
                        aria-label={`${art.label} — Geschoss`}
                        value={detail.geschoss ?? ""}
                        onChange={(e) => setDetail(art.key, { geschoss: e.target.value })}
                      >
                        <option value="">Bitte wählen</option>
                        {geschosse.map((gs) => (
                          <option key={gs} value={gs}>
                            {gs}
                          </option>
                        ))}
                        {detail.geschoss && !geschosse.includes(detail.geschoss) && (
                          <option value={detail.geschoss}>{detail.geschoss}</option>
                        )}
                      </select>
                    </div>
                    {art.key === "tiefgarage" && (
                      <div className={mergeClasses(styles.field, styles.fullSpan)}>
                        <span className={styles.fieldLabel}>Ausdehnung</span>
                        <select
                          className={styles.input}
                          name="stellplatz-tiefgarage-ausdehnung"
                          aria-label="Tiefgarage — Ausdehnung"
                          value={detail.ausdehnung ?? ""}
                          onChange={(e) => setDetail(art.key, { ausdehnung: e.target.value })}
                        >
                          <option value="">Bitte wählen</option>
                          {TIEFGARAGE_AUSDEHNUNG.map((a) => (
                            <option key={a} value={a}>
                              {a}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    {art.key === "tiefgarage" && detail.ausdehnung === TIEFGARAGE_DARUEBER_HINAUS && (
                      <div className={styles.field}>
                        <span className={styles.fieldLabel}>Davon außerhalb des Gebäudes (m²)</span>
                        <input
                          className={styles.input}
                          name="stellplatz-tiefgarage-flaecheAusserhalb"
                          aria-label="Tiefgarage — Fläche außerhalb des Gebäudes (m²)"
                          inputMode="decimal"
                          value={detail.flaecheAusserhalb ?? ""}
                          onChange={(e) =>
                            setDetail(art.key, {
                              flaecheAusserhalb: ziffernFilter(e.target.value, { dezimal: true }),
                            })
                          }
                        />
                      </div>
                    )}
                    {/* Der frühere Freitext bleibt nur sichtbar, solange er
                        Inhalt hat — sonst ginge er beim Bearbeiten verloren. */}
                    {detail.lageWo && (
                      <div className={mergeClasses(styles.field, styles.fullSpan)}>
                        <span className={styles.fieldLabel}>Frühere Angabe: wo im Gebäude?</span>
                        <input
                          className={styles.input}
                          name={`stellplatz-${art.key}-lageWo`}
                          aria-label={`${art.label} — frühere Angabe wo im Gebäude`}
                          value={detail.lageWo}
                          onChange={(e) => setDetail(art.key, { lageWo: e.target.value })}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default StellplatzBlock;
